import React, { useEffect, useRef, useState, useMemo } from "react";

import {
  TranscriptionFollowUpProps,
  TranscriptionPart,
  Word,
} from "./interface";
import {
  Container,
  ContainerTiny,
  ContentTranscription,
  HighlightedWord,
  NormalWord,
} from "./styles";
import { hearingFileService } from "../../../../services/hearingFile";
import { LoadSpinner } from "../../../LoadSpinner";

const TranscriptionFollowUp: React.FC<TranscriptionFollowUpProps> = ({
  hearingFileId,
  videoRef,
}) => {
  const [transcription, setTranscription] = useState<TranscriptionPart[]>([]);
  const [channels, setChannels] = useState<{ _id: number; name: string }[]>(
    []
  );
  const [itemName, setItemName] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const fetchData = async () => {
    if (hearingFileId == null) return;
    setLoading(true);
    setError(null);

    try {
      const response =
        await hearingFileService.getTranscriptionByHearingFileId(
          hearingFileId
        );

      if (response.success && response.body) {
        const transcriptionObj = response.body.item1.transcription;
        const data = transcriptionObj.transcriptionData;
        const parts = data.parts ?? [];

        const rawChannels = data.channels ?? [];
        const channelsData =
          rawChannels.length > 0
            ? rawChannels
            : [
                {
                  _id: parts[0]?.channel ?? 0,
                  name: response.body.item2 || "speaker 1",
                },
              ];

        setChannels(channelsData);
        setTranscription(parts);
        setItemName(response.body.item2 ?? null);
      } else {
        setError(response.message || "Erro desconhecido na transcrição.");
      }
    } catch (err: any) {
      setError(err.message || "Falha ao buscar transcrição.");
      setTranscription([]);
      setChannels([]);
      setItemName(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (hearingFileId != null) {
      fetchData();
    }
  }, [hearingFileId]);

  const mergedTranscription = useMemo(() => {
    const parts = transcription;
    const merged: TranscriptionPart[] = [];
    if (parts.length === 0) return merged;

    let current = { ...parts[0], words: [...parts[0].words] };
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      const lastWord = current.words[current.words.length - 1];
      const firstWord = part.words[0];

      if (
        part.channel === current.channel &&
        firstWord.begin - lastWord.end <= 3
      ) {
        current.words = current.words.concat(part.words);
      } else {
        merged.push(current);
        current = { ...part, words: [...part.words] };
      }
    }
    merged.push(current);
    return merged;
  }, [transcription]);

  useEffect(() => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    let animationFrameId: number;

    const updateTime = () => {
      setCurrentTime(video.currentTime);
      if (contentRef.current) {
        const parent = contentRef.current;
        const activeWordEl = parent.querySelector(
          ".highlighted-word"
        ) as HTMLElement | null;
        if (activeWordEl) {
          const parentHeight = parent.clientHeight;
          const wordOffset = activeWordEl.offsetTop;
          if (
            wordOffset < parent.scrollTop ||
            wordOffset > parent.scrollTop + parentHeight - 30
          ) {
            parent.scrollTop = wordOffset - parentHeight / 2;
          }
        }
      }
      animationFrameId = requestAnimationFrame(updateTime);
    };

    video.addEventListener("play", () => {
      animationFrameId = requestAnimationFrame(updateTime);
    });
    video.addEventListener("pause", () => {
      cancelAnimationFrame(animationFrameId);
    });

    if (!video.paused) {
      animationFrameId = requestAnimationFrame(updateTime);
    }

    video.addEventListener("timeupdate", () => {
      setCurrentTime(video.currentTime);
    });

    return () => {
      video.removeEventListener("play", () => {
        cancelAnimationFrame(animationFrameId);
      });
      video.removeEventListener("pause", () => {
        cancelAnimationFrame(animationFrameId);
      });
      video.removeEventListener("timeupdate", () => {
        setCurrentTime(video.currentTime);
      });
      cancelAnimationFrame(animationFrameId);
    };
  }, [videoRef]);

  const handleWordClick = (word: Word) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = word.begin;
    setCurrentTime(word.begin);
    if (!videoRef.current.paused) {
      videoRef.current.play();
    }
  };

  return (
    <Container>
      <ContainerTiny>
        {loading ? (
          <LoadSpinner loading={loading} />
        ) : error ? (
          <p>{error}</p>
        ) : (
          <ContentTranscription ref={contentRef}>
            {mergedTranscription.length > 0 ? (
              mergedTranscription.map((part) => {

                const found = channels.find((c) => c._id === part.channel);
                const baseName = found?.name.split("@")[0] || itemName || "";

                const displayName =
                  baseName.toLowerCase().startsWith("kenta") && itemName
                    ? itemName
                    : baseName;

                return (
                  <p key={part._id}>
                    {displayName && <strong>{displayName}: </strong>}

                    {part.words.map((word, wIdx) => {
                      const isActive =
                        currentTime >= word.begin - 0.1 &&
                        currentTime <= word.end + 0.1;

                      return isActive ? (
                        <HighlightedWord
                          key={wIdx}
                          className="highlighted-word"
                          onClick={() => handleWordClick(word)}
                        >
                          {word.word}{" "}
                        </HighlightedWord>
                      ) : (
                        <NormalWord
                          key={wIdx}
                          onClick={() => handleWordClick(word)}
                        >
                          {word.word}{" "}
                        </NormalWord>
                      );
                    })}
                  </p>
                );
              })
            ) : (
              <p>Nenhuma transcrição disponível.</p>
            )}
          </ContentTranscription>
        )}
      </ContainerTiny>
    </Container>
  );
};

export default TranscriptionFollowUp;
